import React from 'react';
import { Formik } from 'formik';
import { useAlert } from '../../../../AlertProvider';

import { AppointmentRecurring, AppointmentSlotDuration } from '@uplift/api';
import { useCurrentuser } from '@uplift/contexts';
import { ProviderMedicalRole, Sex } from '@uplift/types';
import { useL10n } from '@uplift/hooks';


import { CreateAppointmentFormValues } from '../AddSessionForm.types';
import { useAddSessionValidationSchema } from '../AddSessionForm.validation';
import { useAddSession } from '@uplift/queries';
import { useQueryClient } from '@tanstack/react-query';
import { providerScheduleViewTypeRoute } from '@uplift/constants';


export type AddSessionFormType = {
  isPsychiatrist: boolean;
  isNewPatient: boolean;
  existingPatient: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    preferredName?: string | undefined;
  } | undefined;
  invitedPatient: {
    firstName: string;
    lastName: string;
    email: string;
    sex?: Sex;
    birthDate?: string;
  };
  duration: AppointmentSlotDuration;
  repeats: AppointmentRecurring;
  date: string | null;
  startTime: string | null;
  notes: string;
};

export type AddSessionFormFormikProps = {
  onSuccess: () => Promise<void>;
};


export const AddSessionFormFormik: React.FC<AddSessionFormFormikProps> = (props) => {
  const queryClient = useQueryClient();
  const l10n = useL10n();
  const alert = useAlert();
  const me = useCurrentuser();
  const shouldInvitePatient = providerScheduleViewTypeRoute.useQueryParam('createAppointment') === 'invite';
  const isPsychiatrist = me.medicalRole === ProviderMedicalRole.psychiatrist;
  const validator = useAddSessionValidationSchema();
  const { addSession } = useAddSession();

  const onSubmit = async (values: CreateAppointmentFormValues, helpers: { setStatus: (e: any) => void }) => {
    helpers.setStatus({
      errorDisplayed: false,
      errorDescription: '',
      submissionBlocked: false,
    });

    try {
      const { patient } = await addSession(values);

      await props.onSuccess?.();
      queryClient.invalidateQueries([ 'calendar' ]);
      if (values.isNewPatient) {
        alert.success(l10n.schedule.createAppointment.invitationSent(patient.preferredName || patient.firstName));
      } else {
        if (values.repeats === AppointmentRecurring.none) {
          alert.success(l10n.schedule.createAppointment.sessionAdded);
        } else {
          alert.success(l10n.schedule.createAppointment.sessionsAdded);
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        helpers.setStatus({
          errorDisplayed: true,
          errorDescription: e?.message || 'unknown',
          submissionBlocked: false,
        });
      } else {
        helpers.setStatus({
          errorDisplayed: true,
          errorDescription: 'unknown',
          submissionBlocked: false,
        });
      }

    }
  };

  const duration = React.useMemo(() => {
    if (isPsychiatrist) {
      return AppointmentSlotDuration.thirty;
    }

    return AppointmentSlotDuration.sixty;
  }, [ isPsychiatrist ]);

  const initialValues: AddSessionFormType = {
    isPsychiatrist: isPsychiatrist,
    isNewPatient: shouldInvitePatient ?? false,
    existingPatient: undefined,
    invitedPatient: {
      firstName: '',
      lastName: '',
      email: '',
      sex: undefined,
      birthDate: undefined
    },
    duration: duration,
    repeats: AppointmentRecurring.none,
    date: null,
    startTime: null,
    notes: '',
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validator}
      initialStatus={{
        errorDisplayed: false,
        errorDescription: '',
        submissionBlocked: false,
      }}
    >
      {props.children}
    </Formik>
  );
};
